import React, { useRef } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Loader2, Check } from "lucide-react";

// Generic multi-step modal shell — any CRUD flow that needs a wizard
// (not just email templates) can adopt this. Step content stays fully
// owned by the caller via `children`; this component only owns the
// chrome: header, numbered step indicator, and Back/Next/Finish nav.
//
// Usage:
//   <StepModal
//     isOpen={isOpen} onClose={onClose} title="Create Template"
//     steps={[{ key: "basic", label: "Basic Info" }, ...]}
//     currentStep={stepIndex}
//     canGoNext={true} canFinish={isVerifiedThisSession}
//     onNext={goNext} onBack={goBack} onFinish={handleFinish}
//     isSubmitting={saving}
//   >
//     {renderActiveStep()}
//   </StepModal>
const StepModal = ({
  isOpen,
  onClose,
  title,
  steps = [],
  currentStep = 0,
  canGoNext = true,
  canFinish = false,
  onNext,
  onBack,
  onFinish,
  finishLabel = "Finish",
  isSubmitting = false,
  closeOnOverlayClick = true,
  children,
}) => {
  const overlayRef = useRef(null);

  if (!isOpen) return null;

  const isLastStep = currentStep === steps.length - 1;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current && !isSubmitting && closeOnOverlayClick) onClose();
  };

  // Portalled to <body> so the overlay is never affected by an ancestor's
  // layout (e.g. a `space-y-*` container adding margin-top, which would shift
  // a position:fixed overlay down) or stacking context — the backdrop always
  // covers the full viewport.
  return createPortal(
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
    >
      <div className="bg-surface border border-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <h3 className="text-sm font-semibold text-text_primary">{title}</h3>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-text_muted disabled:opacity-50 flex-shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-border bg-background flex-shrink-0 overflow-x-auto">
          {steps.map((step, idx) => (
            <React.Fragment key={step.key}>
              <div
                className={`flex items-center gap-1.5 text-xs font-medium whitespace-nowrap ${
                  idx === currentStep ? "text-primary" : idx < currentStep ? "text-text_secondary" : "text-text_muted"
                }`}
              >
                <span
                  className={`flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-semibold border flex-shrink-0 ${
                    idx === currentStep
                      ? "bg-primary text-white border-primary"
                      : idx < currentStep
                      ? "bg-success text-white border-success"
                      : "bg-surface_alt border-border"
                  }`}
                >
                  {idx < currentStep ? <Check size={12} /> : idx + 1}
                </span>
                {step.label}
              </div>
              {idx < steps.length - 1 && <div className="flex-1 h-px bg-border min-w-[16px]" />}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {children}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-border bg-surface_alt flex-shrink-0">
          {currentStep > 0 ? (
            <button
              onClick={onBack}
              disabled={isSubmitting}
              className="inline-flex items-center gap-1 px-4 py-2 text-xs font-semibold rounded-lg bg-surface text-text_secondary border border-border disabled:opacity-50"
            >
              <ChevronLeft size={13} /> Back
            </button>
          ) : (
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-surface text-text_secondary border border-border disabled:opacity-50"
            >
              Cancel
            </button>
          )}

          {isLastStep ? (
            <button
              onClick={onFinish}
              disabled={!canFinish || isSubmitting}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-success text-white disabled:opacity-60 shadow-sm"
            >
              {isSubmitting ? (
                <><Loader2 size={13} className="animate-spin" /> Saving…</>
              ) : (
                <><Check size={13} /> {finishLabel}</>
              )}
            </button>
          ) : (
            <button
              onClick={onNext}
              disabled={!canGoNext || isSubmitting}
              className="inline-flex items-center gap-1 px-4 py-2 text-xs font-semibold rounded-lg bg-primary text-white disabled:opacity-60 shadow-sm"
            >
              Next <ChevronRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StepModal;
